import type { CollectionFieldMigrationMapping, CollectionSummaryJSONObject } from '@ikegps/core-api';
import {
	autoMatchFormFields,
	autoMatchSubformFields,
	canMigrateFieldType,
	FormModelFieldSubForm,
	getSubformPath,
	IkePromiseQueue,
	type CollectionModel,
	type FormModel,
	type FormModelField,
} from '@ikegps/core-shared';
import { action, computed, observable, makeObservable } from 'mobx';
import { MenuComponent } from '../../components/menu/menu';
import { MenuItemComponent, type MenuItem } from '../../components/menu/menu.item';
import { Logger } from '../../services';
import { AppStore } from '../../store';
import {
	computeDropdownLoss,
	computeLostFieldImpact,
	type DropdownFieldLoss,
} from './collection.form.migrate.dropdown.loss';
import { fieldLabel, fieldOptionLabel } from './collection.form.migrate.map.fields.util';

const COLLECTION_FETCH_CONCURRENCY = 10;

/**
 * Tracks the (initially auto-matched, then user-editable) source-field -> target-field mapping for
 * a form migration. Mapping is one-directional: a field's valid target options are whatever its parent
 * subform is currently mapped to, or anything nested deeper within it — never a shallower level. So
 * changing a group's target re-matches everything beneath it against that group's own subtree.
 */
export class MapFieldsState {
	readonly sourceForm: FormModel;
	readonly targetForm: FormModel;

	@observable private mapping = new Map<string, string | null>();
	/** Source field ids whose current mapping came from auto-match and hasn't been touched by the user
	 *  yet — drives the "(auto)" suffix on the target dropdown. Any explicit `setTargetField` call for a
	 *  field clears its own entry, whether the user picks a different target or re-confirms the same one. */
	@observable private autoMatchedFieldIds = new Set<string>();
	@observable.ref private loadedCollections: CollectionModel[] | null = null;
	@observable private searchQueryValue = '';
	private targetFieldsById = new Map<string, FormModelField>();
	private menus = new Map<string, MenuComponent>();

	constructor(sourceForm: FormModel, targetForm: FormModel, collections: CollectionSummaryJSONObject[] = []) {
		makeObservable(this);

		this.sourceForm = sourceForm;
		this.targetForm = targetForm;

		for (const field of targetForm.getFieldsRecursive) {
			this.targetFieldsById.set(field.fieldId, field);
		}

		for (const match of autoMatchFormFields(sourceForm, targetForm)) {
			this.mapping.set(match.sourceField.fieldId, match.targetField?.fieldId ?? null);

			if (match.targetField != null) {
				this.autoMatchedFieldIds.add(match.sourceField.fieldId);
			}
		}

		if (collections.length === 0) {
			this.loadedCollections = [];
		} else {
			// oxlint-disable-next-line no-floating-promises
			this.loadCollections(collections);
		}
	}

	/** Selected collections' actual field data, fetched once up front so the dropdown-value-loss
	 *  badges (and the review step's totals) can be recomputed synchronously as the user edits the
	 *  mapping, instead of re-fetching on every change. Null while the initial fetch is in flight. */
	private async loadCollections(collections: CollectionSummaryJSONObject[]) {
		const queue = new IkePromiseQueue(Logger, COLLECTION_FETCH_CONCURRENCY);

		const loaded = await Promise.all(
			collections.map((summary) =>
				queue.addAndReturn(
					(): Promise<CollectionModel | null> =>
						AppStore.Collection.fetch(summary.revision).catch((error: unknown): null => {
							Logger.error({ error, revision: summary.revision }, 'Failed to fetch collection for dropdown-loss check');
							return null;
						})
				)
			)
		);

		this.setLoadedCollections(loaded.filter((collection): collection is CollectionModel => collection != null));
	}

	@action private setLoadedCollections(collections: CollectionModel[]) {
		this.loadedCollections = collections;
	}

	/** Null while the initial collection fetch is still in flight. */
	@computed private get dropdownLoss() {
		if (this.loadedCollections == null) {
			return null;
		}

		try {
			return computeDropdownLoss(this, this.loadedCollections);
		} catch (error: unknown) {
			Logger.error({ error }, 'Failed to compute dropdown-value loss');
			return { totalLostOccurrences: 0, lossByFieldId: new Map<string, DropdownFieldLoss>() };
		}
	}

	/** Total selected-collection instances whose actual value will be lost — the same metric shown as
	 *  the confirmation step's "dropdown values lost" total. Null while still loading. */
	get dropdownValuesLostTotal(): number | null {
		return this.dropdownLoss?.totalLostOccurrences ?? null;
	}

	/** Distinct values that will be lost for a given source field, for its badge on the map-fields step. */
	getDropdownLossFor(field: FormModelField): DropdownFieldLoss | null {
		return this.dropdownLoss?.lossByFieldId.get(field.fieldId) ?? null;
	}

	/** All fields with at least one dropdown value that will be lost, for the confirmation step's list. */
	get dropdownFieldsLost(): DropdownFieldLoss[] {
		return this.dropdownLoss == null ? [] : [...this.dropdownLoss.lossByFieldId.values()];
	}

	/** Null while the initial collection fetch is still in flight. */
	@computed private get lostFieldImpact(): Map<string, number> | null {
		if (this.loadedCollections == null) {
			return null;
		}

		try {
			return computeLostFieldImpact(this.lostFields, this.loadedCollections);
		} catch (error: unknown) {
			Logger.error({ error }, 'Failed to compute lost-field collection impact');
			return new Map();
		}
	}

	/** How many selected collections actually hold a value for a fully-unmapped (entirely lost) field —
	 *  for the confirmation step's "fields that will be lost" list. Null while still loading. */
	getCollectionsImpactedFor(field: FormModelField): number | null {
		const impact = this.lostFieldImpact;
		return impact == null ? null : (impact.get(field.fieldId) ?? 0);
	}

	/** Whether a source field's current mapping is still the unconfirmed auto-match suggestion, rather
	 *  than something the user explicitly picked (or re-picked) for it. */
	isAutoMatched(sourceField: FormModelField): boolean {
		return this.autoMatchedFieldIds.has(sourceField.fieldId);
	}

	get searchQuery(): string {
		return this.searchQueryValue;
	}

	@action setSearchQuery(query: string) {
		this.searchQueryValue = query;
	}

	/** Whether a field's own name matches the current search query — always true when the query is
	 *  blank. Doesn't consider ancestors/descendants; the view combines this per field as it walks the
	 *  tree, so a matching subform reveals its whole subtree and a matching leaf stays visible within an
	 *  otherwise-filtered-out group. */
	matchesSearchQuery(field: FormModelField): boolean {
		const query = this.searchQueryValue.trim().toLowerCase();
		return query === '' || fieldLabel(field).toLowerCase().includes(query);
	}

	getTargetField(sourceField: FormModelField): FormModelField | null {
		const targetFieldId = this.mapping.get(sourceField.fieldId);
		if (targetFieldId == null) {
			return null;
		}

		return this.targetFieldsById.get(targetFieldId) ?? null;
	}

	/** Source leaf fields with no target mapping — the data that will be lost on migration. Groups
	 *  are containers with no data of their own, so only leaf fields are counted as "lost". */
	@computed get lostFields(): FormModelField[] {
		return this.sourceForm.getFieldsRecursive
			.filter((field) => !FormModelFieldSubForm.isSubForm(field))
			.filter((field) => this.getTargetField(field) == null);
	}

	/** How many of `lostFields` sit (at any depth) within the given subform, for its group-row badge. */
	countLostFieldsIn(subform: FormModelFieldSubForm): number {
		const descendantIds = new Set(subform.getFieldsRecursive.map((field) => field.fieldId));
		return this.lostFields.filter((field) => descendantIds.has(field.fieldId)).length;
	}

	/** Valid target candidates for a source field: same kind (group/leaf), a type the field's data can
	 *  migrate onto, at or beneath its parent's current target mapping (one-directional — never a
	 *  shallower level than the source field itself), and not already claimed by another source field.
	 *  Migration moves values without rewriting them, so a target whose type the data cannot be carried
	 *  onto would silently drop the data it was chosen to preserve. */
	getOptions(sourceField: FormModelField): FormModelField[] {
		const isGroup = FormModelFieldSubForm.isSubForm(sourceField);
		const usedTargetFieldIds = this.getUsedTargetFieldIds(sourceField.fieldId);

		return this.getTargetScope(sourceField).filter(
			(candidate) =>
				FormModelFieldSubForm.isSubForm(candidate) === isGroup &&
				canMigrateFieldType(sourceField.type, candidate.type) &&
				!usedTargetFieldIds.has(candidate.fieldId)
		);
	}

	/** The confirmed mapping, in the shape the migrate action executes. Walks the same field list the
	 *  modal renders, so what is sent is exactly what was reviewed. Each source field is addressed by its
	 *  path from the form root, since a field id is only unique within its own subform. */
	toFieldMappingPayload(): CollectionFieldMigrationMapping[] {
		return this.sourceForm.getFieldsRecursive.map((sourceField) => ({
			sourcePath: [...getSubformPath(sourceField).map((subform) => subform.id), sourceField.id],
			targetFieldId: this.getTargetField(sourceField)?.id ?? null,
		}));
	}

	@action setTargetField(sourceField: FormModelField, targetField: FormModelField | null) {
		this.mapping.set(sourceField.fieldId, targetField?.fieldId ?? null);
		this.autoMatchedFieldIds.delete(sourceField.fieldId);

		if (FormModelFieldSubForm.isSubForm(sourceField)) {
			this.rematchDescendants(sourceField, targetField);
		}
	}

	/**
	 * A group's contents can only map inside whatever that group maps to, so changing its target
	 * invalidates every mapping beneath it. Rather than just dropping them, re-run the auto-match for
	 * that subtree against the new group — so re-selecting a group that was set to "not mapped"
	 * proposes its fields again instead of leaving them all unmatched.
	 */
	private rematchDescendants(sourceSubform: FormModelFieldSubForm, targetField: FormModelField | null) {
		const targetSubform = targetField != null && FormModelFieldSubForm.isSubForm(targetField) ? targetField : null;

		const matches = autoMatchSubformFields(
			sourceSubform,
			targetSubform,
			this.sourceForm,
			this.targetForm,
			this.getUsedTargetFieldIdsOutside(sourceSubform)
		);

		for (const match of matches) {
			this.mapping.set(match.sourceField.fieldId, match.targetField?.fieldId ?? null);

			if (match.targetField != null) {
				this.autoMatchedFieldIds.add(match.sourceField.fieldId);
			} else {
				this.autoMatchedFieldIds.delete(match.sourceField.fieldId);
			}
		}
	}

	/** Targets claimed by fields outside the given subform, which its contents may not take. */
	private getUsedTargetFieldIdsOutside(sourceSubform: FormModelFieldSubForm): Set<string> {
		const descendantFieldIds = new Set(sourceSubform.getFieldsRecursive.map((field) => field.fieldId));
		const used = new Set<string>();

		for (const [sourceFieldId, targetFieldId] of this.mapping.entries()) {
			if (targetFieldId != null && !descendantFieldIds.has(sourceFieldId)) {
				used.add(targetFieldId);
			}
		}

		return used;
	}

	/** Menu instance for a field's target dropdown. Cached per field so its open/keyboard-nav state
	 *  persists across re-renders instead of resetting every time the tree redraws. */
	getMenu(sourceField: FormModelField): MenuComponent {
		let menu = this.menus.get(sourceField.fieldId);

		if (menu == null) {
			menu = new MenuComponent({ items: () => this.getMenuItems(sourceField) });
			this.menus.set(sourceField.fieldId, menu);
		}

		return menu;
	}

	private getMenuItems(sourceField: FormModelField): MenuItem[] {
		const currentTargetId = this.mapping.get(sourceField.fieldId);

		return this.getOptions(sourceField).map((option) => ({
			view: MenuItemComponent.view([fieldOptionLabel(option)]),
			action: () => this.setTargetField(sourceField, option),
			selected: option.fieldId === currentTargetId,
		}));
	}

	/** The target fields eligible for a source field: every target field for a root-level source field
	 *  (mapping is one-directional — data may move down into deeper nesting but never back up to a
	 *  shallower level, and a root field has no shallower level to be excluded from), or every field at
	 *  or beneath the source field's parent subform's own current target. */
	private getTargetScope(sourceField: FormModelField): FormModelField[] {
		if (sourceField.parent == null || sourceField.parent === this.sourceForm) {
			return this.targetForm.getFieldsRecursive;
		}

		const parentTarget = this.getTargetField(sourceField.parent);
		if (parentTarget != null && FormModelFieldSubForm.isSubForm(parentTarget)) {
			return parentTarget.getFieldsRecursive;
		}

		return [];
	}

	private getUsedTargetFieldIds(excludeSourceFieldId: string): Set<string> {
		const used = new Set<string>();

		for (const [sourceFieldId, targetFieldId] of this.mapping.entries()) {
			if (sourceFieldId !== excludeSourceFieldId && targetFieldId != null) {
				used.add(targetFieldId);
			}
		}

		return used;
	}
}
